import {
  CONTRACT_STATUSES,
  GIOI_HAN_FILE_HOP_DONG_BYTES,
  NGUONG_CHI_TRA_ADSENSE,
  PARTNER_KIND_LABEL,
  THUE_MY_MAC_DINH_PCT,
  laAdsense,
  STATEMENT_STATUSES,
  STATEMENT_STATUS_LABEL,
  TRUONG_SAO_KE_LABEL,
  laLoiSoHopDongDaXoa,
  hopDongPhuNgay,
  type PartnerChannelRow,
  type PartnerContract,
  type Statement,
  type StatementStatus,
} from '@qlk/shared';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { HangHopDong } from '../components/HangHopDong.js';
import { MucHopDongDaXoa } from '../components/MucHopDongDaXoa.js';
import { HangSuaLink } from '../components/HangSuaLink.js';

import { coQuyen, useToi } from '../../../lib/auth.js';
import {
  Avatar,
  type CotBang,
  DataTable,
  EmptyState,
  Money,
  ngayDay,
  Nut,
  PageHeader,
  StatusBadge,
  Tabs,
  homNayVn,
  ngayGio,
} from '../../../ui/index.js';
import {
  docTepThanhBase64,
  useDanhSachKenhChon,
  useDoiTrangThaiSaoKe,
  useGanKenh,
  useHopDong,
  useKenhCuaDoiTac,
  useKhoiPhucHopDong,
  useLuuHopDong,
  useLuuTemplate,
  useMotDoiTac,
  useSaoKe,
  useTaiTepHopDong,
  useTaoTicket,
  useTemplate,
  useNhatKyHopDong,
} from '../api.js';
import { O, OChonTruong, OThongTin } from '../../../ui/index.js';
import { tenKy } from '../../../lib/ky.js';

const NHAN_TRANG_THAI_HD: Record<(typeof CONTRACT_STATUSES)[number], string> = {
  active: 'Đang hiệu lực',
  expired: 'Hết hạn',
  pending: 'Chờ ký',
};

const TONE_SAO_KE: Record<StatementStatus, 'ok' | 'warn' | 'info' | 'neutral'> = {
  pending: 'warn',
  reconciled: 'info',
  confirmed: 'info',
  held: 'neutral',
  paid: 'ok',
};

/**
 * Tài khoản AdSense không có hợp đồng giấy: tab đó là "Cài đặt AdSense" (ngày
 * hiệu lực, thuế Mỹ, ngưỡng chi trả) — vẫn cùng một bảng và cùng luật
 * sửa/xoá/phiên bản với hợp đồng Network.
 */
const TAB_HOP_DONG = { mcn: 'Hợp đồng', adsense: 'Cài đặt AdSense' } as const;

const TAB = [
  { id: 'hop-dong', label: TAB_HOP_DONG.mcn },
  { id: 'kenh', label: 'Kênh' },
  { id: 'sao-ke', label: 'Sao kê' },
  { id: 'template', label: 'Template import' },
  { id: 'ho-tro', label: 'Yêu cầu hỗ trợ' },
];

export function TrangChiTietDoiTac() {
  const { id = '' } = useParams();
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'networks', 'edit');
  // Sửa / xoá hợp đồng là việc của quản trị viên: đổi tỷ lệ là đổi tiền.
  const coQuanTri = coQuyen(toi.data?.permissions, 'networks', 'admin');

  const doiTac = useMotDoiTac(id);
  /*
   * `?tab=kenh` để chỗ khác trỏ thẳng vào tab Kênh — bảng lịch sử Network của
   * trang kênh dùng khi link chưa gắn hợp đồng. Chỉ nhận id có trong `TAB`.
   */
  const [thamSo] = useSearchParams();
  const tabBanDau = thamSo.get('tab');
  const [tab, datTab] = useState(
    TAB.some((t) => t.id === tabBanDau) ? (tabBanDau ?? 'hop-dong') : 'hop-dong',
  );
  const adsense = doiTac.data ? laAdsense(doiTac.data.kind) : false;

  if (doiTac.isPending) return <p className="text-co-ban text-chu-phu">Đang tải đối tác…</p>;

  if (doiTac.error) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState
          loi
          title="Không mở được đối tác"
          description={doiTac.error.message}
          action={
            <Link to="/network" className="text-co-ban font-semibold text-nhan">
              Về danh sách Network
            </Link>
          }
        />
      </div>
    );
  }

  const d = doiTac.data;

  return (
    <>
      <PageHeader
        breadcrumb={['VẬN HÀNH', 'Network (MCN)']}
        title={d.name}
        description={`${PARTNER_KIND_LABEL[d.kind]}${d.category === 'music' ? ' · nhạc' : ''}`}
        actions={
          <>
            <Link to="/network" className="text-co-ban font-semibold text-chu-phu hover:text-chu">
              ← Danh sách
            </Link>
            {coSua && (
              <Link
                to={`/network/${d.id}/sua`}
                className="vung-bam inline-flex items-center justify-center rounded-[10px] bg-nhan px-4 text-co-ban font-semibold text-white hover:bg-nhan-dam"
              >
                Sửa đối tác
              </Link>
            )}
          </>
        }
      />

      <section className="mb-6 flex flex-wrap items-center gap-6 rounded-the border border-vien bg-be-mat p-6">
        <span
          className="so-lieu inline-flex size-14 shrink-0 items-center justify-center rounded-[12px] text-tieu-de-khoi font-bold text-white"
          style={{ backgroundColor: d.logoColor }}
          aria-hidden="true"
        >
          {d.name.slice(0, 2).toUpperCase()}
        </span>
        <dl className="flex flex-wrap gap-8">
          <OThongTin nhan="Số kênh đang gán">{d.channelCount}</OThongTin>
          {adsense ? (
            <>
              <OThongTin nhan="Mã nhà xuất bản">
                {d.publisherCode ?? <span className="text-chu-mo">—</span>}
              </OThongTin>
              <OThongTin nhan="Thuế Mỹ khấu trừ">
                {d.thueMyPct === null ? '—' : `${d.thueMyPct.replace('.', ',')}%`}
              </OThongTin>
            </>
          ) : (
            <>
              <OThongTin nhan="Tỷ lệ Network giữ">
                {d.defaultSharePercent === null
                  ? '—'
                  : `${d.defaultSharePercent.replace('.', ',')}%`}
              </OThongTin>
              <OThongTin nhan="Thuế Mỹ khấu trừ">
                {d.thueMyPct === null ? '—' : `${d.thueMyPct.replace('.', ',')}%`}
              </OThongTin>
            </>
          )}
          <OThongTin nhan="Hợp đồng">
            {d.contractStatus ? (
              <StatusBadge tone={d.contractStatus === 'active' ? 'ok' : 'neutral'}>
                {NHAN_TRANG_THAI_HD[d.contractStatus]}
              </StatusBadge>
            ) : (
              '—'
            )}
          </OThongTin>
          <OThongTin nhan="Liên hệ">
            {d.contactEmail ?? d.contactName ?? <span className="text-chu-mo">—</span>}
          </OThongTin>
        </dl>
      </section>

      <section className="rounded-the border border-vien bg-be-mat p-6">
        <Tabs
          aria-label="Thông tin đối tác"
          items={TAB.map((t) =>
            t.id === 'hop-dong' && adsense ? { ...t, label: TAB_HOP_DONG.adsense } : t,
          )}
          value={tab}
          onChange={datTab}
        />
        <div className="mt-6">
          {tab === 'hop-dong' && (
            <TabHopDong partnerId={id} coSua={coSua} coQuanTri={coQuanTri} adsense={adsense} />
          )}
          {tab === 'kenh' && <TabKenh partnerId={id} coSua={coSua} adsense={adsense} />}
          {tab === 'sao-ke' && <TabSaoKe partnerId={id} coSua={coSua} />}
          {tab === 'template' && <TabTemplate partnerId={id} coSua={coSua} />}
          {tab === 'ho-tro' && <TabHoTro partnerId={id} coSua={coSua} />}
        </div>
      </section>
    </>
  );
}

/* ------------------------------ Hợp đồng ------------------------------ */

function TabHopDong({
  partnerId,
  coSua,
  coQuanTri,
  adsense,
}: {
  partnerId: string;
  coSua: boolean;
  coQuanTri: boolean;
  adsense: boolean;
}) {
  const hopDong = useHopDong(partnerId);
  const luu = useLuuHopDong(partnerId);
  const taiTep = useTaiTepHopDong(partnerId);

  const [mo, datMo] = useState(false);
  const [form, datForm] = useState({
    contractNo: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    defaultSharePercent: '20',
    thueMyPct: String(THUE_MY_MAC_DINH_PCT),
    paymentTerms: '',
    paymentThreshold: String(NGUONG_CHI_TRA_ADSENSE),
    fileUrl: '',
    status: 'active' as (typeof CONTRACT_STATUSES)[number],
  });
  const [loiTep, datLoiTep] = useState<string | null>(null);

  /*
   * Số hợp đồng vừa gõ thuộc một bản **đã xoá** còn trong hạn khôi phục: API
   * trả `CONFLICT` kèm `details.ma`, hỏi lại thay vì âm thầm tạo bản thứ hai
   * cùng số.
   */
  const trungBanDaXoa = laLoiSoHopDongDaXoa(luu.error?.details) ? luu.error.details : null;
  const khoiPhuc = useKhoiPhucHopDong(trungBanDaXoa?.contractId ?? '');

  /*
   * Thân request của cả hai nút lưu (gửi form và "tạo hợp đồng mới cùng số").
   *
   * Tài khoản AdSense **không gửi** `contractNo` và `defaultSharePercent`: API
   * tự sinh số `ADS-<ngày hiệu lực>` và ép tỷ lệ 0. Gửi số 0 từ đây thì giao
   * diện lại thành nơi quyết định luật, mà route này còn gọi được bằng API.
   */
  function thanLuu() {
    return {
      ...(adsense ? {} : { contractNo: form.contractNo.trim() }),
      startDate: new Date(`${form.startDate}T00:00:00.000Z`).toISOString(),
      endDate: form.endDate ? new Date(`${form.endDate}T00:00:00.000Z`).toISOString() : null,
      ...(adsense ? {} : { defaultSharePercent: Number(form.defaultSharePercent) }),
      thueMyPct: Number(form.thueMyPct),
      paymentTerms: adsense ? null : form.paymentTerms.trim() || null,
      paymentThreshold: form.paymentThreshold ? Number(form.paymentThreshold) : null,
      currency: 'USD',
      fileUrl: adsense ? null : form.fileUrl || null,
      status: form.status,
    };
  }

  async function chonTep(file: File): Promise<void> {
    datLoiTep(null);
    if (file.size > GIOI_HAN_FILE_HOP_DONG_BYTES) {
      datLoiTep('File hợp đồng vượt quá 20 MB, hãy chia nhỏ hoặc nén lại file');
      return;
    }
    const contentBase64 = await docTepThanhBase64(file);
    const ketQua = await taiTep.mutateAsync({ fileName: file.name, contentBase64 });
    datForm((f) => ({ ...f, fileUrl: ketQua.fileUrl }));
  }

  const cot: CotBang<PartnerContract>[] = [
    {
      key: 'so',
      header: 'Số hợp đồng',
      render: (c) => <span className="so-lieu">{c.contractNo}</span>,
    },
    {
      key: 'hieuLuc',
      header: 'Hiệu lực',
      render: (c) =>
        `${ngayDay(c.startDate)} → ${c.endDate ? ngayDay(c.endDate) : 'không thời hạn'}`,
    },
    ...(adsense
      ? []
      : [
          {
            key: 'tyLe',
            header: 'Network giữ',
            align: 'right' as const,
            render: (c: PartnerContract) => (
              <span className="so-lieu">{c.defaultSharePercent.replace('.', ',')}%</span>
            ),
          },
        ]),
    {
      key: 'thueMy',
      header: 'Thuế Mỹ',
      align: 'right',
      render: (c) => <span className="so-lieu">{c.thueMyPct.replace('.', ',')}%</span>,
    },
    ...(adsense
      ? []
      : [
          {
            key: 'kyThanhToan',
            header: 'Kỳ thanh toán',
            render: (c: PartnerContract) => c.paymentTerms ?? '—',
          },
        ]),
    {
      key: 'nguong',
      header: 'Ngưỡng chi trả',
      align: 'right',
      render: (c) => <Money value={c.paymentThreshold} />,
    },
    ...(adsense
      ? []
      : [
          {
            key: 'file',
            header: 'File',
            render: (c: PartnerContract) =>
              c.fileUrl ? (
                <a
                  href={c.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-nhan"
                >
                  Mở file
                </a>
              ) : (
                <span className="text-chu-mo">—</span>
              ),
          },
        ]),
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (c) => (
        <StatusBadge tone={c.status === 'active' ? 'ok' : 'neutral'}>
          {NHAN_TRANG_THAI_HD[c.status]}
        </StatusBadge>
      ),
    },
    ...(coQuanTri
      ? [
          {
            key: 'sua',
            header: '',
            align: 'right' as const,
            render: (c: PartnerContract) => <HangHopDong hopDong={c} adsense={adsense} />,
          },
        ]
      : []),
  ];

  return (
    <>
      {coSua && (
        <div className="mb-4">
          <Nut
            onClick={() => {
              datMo(!mo);
            }}
          >
            {mo
              ? 'Đóng biểu mẫu'
              : adsense
                ? '+ Thêm hoặc cập nhật cài đặt AdSense'
                : '+ Thêm hoặc cập nhật hợp đồng'}
          </Nut>
        </div>
      )}

      {mo && (
        <form
          className="mb-6 rounded-[12px] border border-vien p-4"
          onSubmit={(e) => {
            e.preventDefault();
            luu.mutate(thanLuu(), {
              onSuccess: () => {
                datMo(false);
              },
            });
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {!adsense && (
              <O
                label="Số hợp đồng"
                value={form.contractNo}
                onChange={(v) => {
                  datForm({ ...form, contractNo: v });
                }}
                required
                moTa="Trùng số hợp đồng là sửa bản cũ; hợp đồng đã chia tiền thì chỉ sửa được phần hành chính."
              />
            )}
            <OChonTruong
              label="Trạng thái"
              value={form.status}
              onChange={(v) => {
                datForm({ ...form, status: v as (typeof CONTRACT_STATUSES)[number] });
              }}
              options={CONTRACT_STATUSES.map((s) => ({ value: s, label: NHAN_TRANG_THAI_HD[s] }))}
            />
            <O
              label="Ngày bắt đầu"
              type="date"
              value={form.startDate}
              onChange={(v) => {
                datForm({ ...form, startDate: v });
              }}
              required
            />
            <O
              label="Ngày kết thúc"
              type="date"
              value={form.endDate}
              onChange={(v) => {
                datForm({ ...form, endDate: v });
              }}
            />
            {!adsense && (
              <O
                label="Network giữ lại (%)"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={form.defaultSharePercent}
                onChange={(v) => {
                  datForm({ ...form, defaultSharePercent: v });
                }}
                required
              />
            )}

            <O
              label="Thuế Mỹ khấu trừ (%)"
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={form.thueMyPct}
              onChange={(v) => {
                datForm({ ...form, thueMyPct: v });
              }}
              required
              moTa="Google giữ trên phần doanh thu phát sinh ở Mỹ. Đã khai W-8 xong thì đặt 0."
            />
            <O
              label="Ngưỡng chi trả (USD)"
              type="number"
              min={0}
              value={form.paymentThreshold}
              onChange={(v) => {
                datForm({ ...form, paymentThreshold: v });
              }}
            />
            {!adsense && (
              <O
                label="Kỳ thanh toán"
                value={form.paymentTerms}
                onChange={(v) => {
                  datForm({ ...form, paymentTerms: v });
                }}
                className="sm:col-span-2"
                moTa="Ví dụ: NET 60, chuyển khoản USD."
              />
            )}

            <div className={adsense ? 'hidden' : 'sm:col-span-2'}>
              <label
                htmlFor="tep-hop-dong"
                className="mb-1 block text-phu font-medium text-chu-phu"
              >
                File hợp đồng (tối đa 20 MB)
              </label>
              <input
                id="tep-hop-dong"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                className="vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 py-2 text-co-ban"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void chonTep(file);
                }}
              />
              {taiTep.isPending && <p className="mt-1 text-phu text-chu-mo">Đang tải file…</p>}
              {form.fileUrl && (
                <p className="mt-1 text-phu text-ok-chu">Đã tải lên: {form.fileUrl}</p>
              )}
              {(loiTep ?? taiTep.error) && (
                <p className="mt-1 text-phu text-bad-chu">{loiTep ?? taiTep.error?.message}</p>
              )}
            </div>
          </div>

          {luu.error && (
            <p
              role="alert"
              className="mt-3 rounded-[10px] bg-bad-nen px-4 py-2 text-co-ban text-bad-chu"
            >
              {luu.error.message}
            </p>
          )}

          {trungBanDaXoa && (
            <div className="mt-3 rounded-[10px] border border-warn-chu/40 bg-warn-nen px-4 py-3">
              <p className="text-co-ban text-warn-chu">
                Hợp đồng số {trungBanDaXoa.contractNo} đã xoá ngày{' '}
                {ngayDay(trungBanDaXoa.deletedAt)}, còn khôi phục được đến{' '}
                {ngayDay(trungBanDaXoa.khoiPhucDenNgay)}.
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <Nut
                  kieu="chinh"
                  dangChay={khoiPhuc.isPending}
                  onClick={() => {
                    khoiPhuc.mutate(undefined, {
                      onSuccess: () => {
                        luu.reset();
                        datMo(false);
                      },
                    });
                  }}
                >
                  Khôi phục bản đã xoá
                </Nut>
                <Nut
                  kieu="phu"
                  dangChay={luu.isPending}
                  onClick={() => {
                    luu.mutate({ ...thanLuu(), boQuaSoDaXoa: true });
                  }}
                >
                  Tạo hợp đồng mới cùng số
                </Nut>
              </div>

              {khoiPhuc.error && (
                <p role="alert" className="mt-2 text-phu text-bad-chu">
                  {khoiPhuc.error.message}
                </p>
              )}
            </div>
          )}

          <div className="mt-4">
            <Nut kieu="chinh" type="submit" dangChay={luu.isPending}>
              Lưu hợp đồng
            </Nut>
          </div>
        </form>
      )}

      <DataTable
        caption="Hợp đồng của đối tác"
        columns={cot}
        rows={hopDong.data ?? []}
        keyOf={(c) => c.id}
        dangTai={hopDong.isPending}
        khiRong={<EmptyState title="Chưa có hợp đồng nào" />}
      />

      {/* Nhật ký thay đổi: đổi tỷ lệ là đổi tiền, phải tra lại được ai sửa gì. */}
      {(hopDong.data ?? []).map((c) => (
        <NhatKyHopDongCuaHopDong key={c.id} hopDong={c} />
      ))}

      {coQuanTri && <MucHopDongDaXoa partnerId={partnerId} />}
    </>
  );
}

/** Nhật ký thay đổi của một hợp đồng; không có thay đổi nào thì không hiện gì. */
function NhatKyHopDongCuaHopDong({ hopDong }: { hopDong: PartnerContract }) {
  const nhatKy = useNhatKyHopDong(hopDong.id);
  const dong = nhatKy.data ?? [];

  if (dong.length === 0) return null;

  const NHAN_VIEC: Record<string, string> = {
    'networks.contract.save': 'Thêm hoặc cập nhật',
    'networks.contract.update': 'Sửa hợp đồng',
    'networks.contract.version': 'Tạo phiên bản mới',
    'networks.contract.delete': 'Xoá hợp đồng',
  };

  return (
    <section className="mt-6 rounded-[12px] border border-vien p-4">
      <h3 className="mb-3 text-co-ban font-semibold text-chu">
        Nhật ký thay đổi · {hopDong.contractNo}
      </h3>

      <ul className="space-y-3">
        {dong.map((n) => (
          <li key={n.id} className="text-phu">
            <p className="text-chu-phu">
              {NHAN_VIEC[n.action] ?? n.action} · {n.nguoiSua ?? 'không rõ'} · {ngayGio(n.luc)}
            </p>
            {n.thayDoi.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-chu-mo">
                {n.thayDoi.map((t) => (
                  <li key={t.truong}>
                    {t.truong}: <span className="so-lieu">{t.cu}</span> →{' '}
                    <span className="so-lieu text-chu">{t.moi}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------- Kênh -------------------------------- */

/**
 * Ô cột "Hợp đồng" của một dòng link: số hợp đồng, hoặc chip vàng khi chưa gắn.
 *
 * Chưa gắn **không** phải chuyện nhỏ: tỷ lệ Network của link về 0 % và thuế Mỹ
 * rơi về mặc định 30 % (ca thật 16/10/2026, kênh Lluvia Serenata của
 * BerlinTomek). Dòng đã rời Network cũng hiện chip — kỳ cũ của nó cũng tính sai.
 */
function ONhanHopDong({ dong }: { dong: PartnerChannelRow }) {
  if (dong.contractNo === null) {
    return <StatusBadge tone="warn">Chưa gắn hợp đồng</StatusBadge>;
  }
  return <span className="so-lieu text-chu">{dong.contractNo}</span>;
}

function TabKenh({
  partnerId,
  coSua,
  adsense,
}: {
  partnerId: string;
  coSua: boolean;
  adsense: boolean;
}) {
  const kenh = useKenhCuaDoiTac(partnerId);
  const hopDong = useHopDong(partnerId);
  const danhSachHopDong = hopDong.data ?? [];

  const cot: CotBang<PartnerChannelRow>[] = [
    {
      key: 'kenh',
      header: 'Kênh',
      render: (d) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={d.channelName} color={d.avatarColor} />
          <div className="min-w-0">
            <Link
              to={`/kenh/${d.channelId}`}
              className="block truncate font-semibold text-chu hover:text-nhan"
            >
              {d.channelName}
            </Link>
            <p className="truncate text-phu text-chu-mo">{d.channelHandle}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'hopDong',
      header: adsense ? 'Cài đặt AdSense' : 'Hợp đồng',
      render: (d) => <ONhanHopDong dong={d} />,
    },
    {
      key: 'tyLe',
      header: 'Tỷ lệ',
      align: 'right',
      render: (d) => d.splitLabel ?? (adsense ? 'Không chia (AdSense)' : '—'),
    },
    {
      key: 'tuNgay',
      header: 'Từ ngày',
      render: (d) => <span className="so-lieu">{ngayDay(d.joinedAt)}</span>,
    },
    {
      key: 'denNgay',
      header: 'Đến ngày',
      render: (d) =>
        d.leftAt ? (
          <span className="so-lieu">{ngayDay(d.leftAt)}</span>
        ) : (
          <StatusBadge tone="ok">Đang hiệu lực</StatusBadge>
        ),
    },
    ...(coSua
      ? [
          {
            key: 'sua',
            header: '',
            align: 'right' as const,
            render: (d: PartnerChannelRow) => (
              <HangSuaLink dong={d} hopDong={danhSachHopDong} adsense={adsense} />
            ),
          },
        ]
      : []),
  ];

  /*
   * Link đang hiệu lực mà chưa gắn hợp đồng là số tiền đang sai (tỷ lệ Network
   * 0 %, thuế Mỹ mặc định) — đẩy lên đầu tab thay vì để người dùng tự soi bảng.
   */
  const thieuHopDong = (kenh.data ?? []).filter((d) => d.leftAt === null && d.contractId === null);

  return (
    <>
      {thieuHopDong.length > 0 && (
        <p role="status" className="mb-4 rounded-chip bg-warn-nen px-3 py-2 text-phu text-warn-chu">
          {thieuHopDong.length === 1
            ? `Kênh ${thieuHopDong[0]?.channelName ?? ''} chưa gắn hợp đồng`
            : `${String(thieuHopDong.length)} kênh chưa gắn hợp đồng`}{' '}
          — tỷ lệ Network của những kênh đó tính 0% và thuế Mỹ lấy mặc định {THUE_MY_MAC_DINH_PCT}%.
          Dùng nút “Sửa ngày hiệu lực / tỷ lệ” ở cuối hàng để chọn hợp đồng.
        </p>
      )}

      {coSua && <FormGanKenh partnerId={partnerId} hopDong={danhSachHopDong} adsense={adsense} />}

      <DataTable
        caption="Kênh của đối tác, gồm cả lịch sử"
        columns={cot}
        rows={kenh.data ?? []}
        keyOf={(d) => d.linkId}
        dangTai={kenh.isPending}
        khiRong={<EmptyState title="Chưa có kênh nào thuộc đối tác này" />}
      />
    </>
  );
}

/* ------------------------------- Sao kê ------------------------------- */

function TabSaoKe({ partnerId, coSua }: { partnerId: string; coSua: boolean }) {
  const saoKe = useSaoKe({ partnerId });
  const doiTrangThai = useDoiTrangThaiSaoKe();

  const cot: CotBang<Statement>[] = [
    { key: 'ky', header: 'Kỳ', render: (s) => <span className="so-lieu">{tenKy(s.period)}</span> },
    { key: 'gop', header: 'Gộp', align: 'right', render: (s) => <Money value={s.grossTotal} /> },
    {
      key: 'phanNw',
      header: 'Phần NW',
      align: 'right',
      render: (s) => <Money value={s.partnerShareTotal} />,
    },
    {
      key: 'thucNhan',
      header: 'Thực nhận',
      align: 'right',
      render: (s) => <Money value={s.netTotal} />,
    },
    { key: 'file', header: 'Nguồn', render: (s) => s.sourceFile ?? '—' },
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (s) =>
        coSua ? (
          <select
            aria-label={`Trạng thái sao kê ${tenKy(s.period)}`}
            value={s.status}
            onChange={(e) => {
              doiTrangThai.mutate({ id: s.id, status: e.target.value as StatementStatus });
            }}
            className="vung-bam rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu"
          >
            {STATEMENT_STATUSES.map((t) => (
              <option key={t} value={t}>
                {STATEMENT_STATUS_LABEL[t]}
              </option>
            ))}
          </select>
        ) : (
          <StatusBadge tone={TONE_SAO_KE[s.status]}>{STATEMENT_STATUS_LABEL[s.status]}</StatusBadge>
        ),
    },
  ];

  return (
    <>
      {coSua && (
        <div className="mb-4">
          <Link
            to={`/network/sao-ke/moi?partnerId=${partnerId}`}
            className="vung-bam inline-flex items-center justify-center rounded-[10px] bg-nhan px-4 text-co-ban font-semibold text-white hover:bg-nhan-dam"
          >
            Tải sao kê mới
          </Link>
        </div>
      )}
      <DataTable
        caption="Lịch sử sao kê của đối tác"
        columns={cot}
        rows={saoKe.data ?? []}
        keyOf={(s) => s.id}
        dangTai={saoKe.isPending}
        khiRong={<EmptyState title="Chưa có sao kê nào" />}
      />
    </>
  );
}

/* ------------------------------ Template ------------------------------ */

function TabTemplate({ partnerId, coSua }: { partnerId: string; coSua: boolean }) {
  const template = useTemplate(partnerId);
  const luu = useLuuTemplate(partnerId);

  const [form, datForm] = useState({
    name: '',
    channelRef: 'Channel',
    gross: 'Gross Revenue',
    partnerShare: 'Network Share',
    net: 'Net Payout',
    headerRow: '1',
    sheet: '',
    delimiter: ',',
  });

  return (
    <>
      <p className="mb-4 text-co-ban text-chu-phu">
        Mỗi Network có định dạng file khác nhau. Lưu ánh xạ cột ở đây để lần import sau không phải
        chọn lại.
      </p>

      <ul className="mb-6 flex flex-col gap-3">
        {(template.data ?? []).map((t) => (
          <li key={t.id} className="rounded-[12px] border border-vien p-4">
            <p className="font-semibold text-chu">{t.name}</p>
            <dl className="mt-2 grid grid-cols-1 gap-2 text-phu text-chu-phu @min-[600px]/noi-dung:grid-cols-2 @min-[900px]/noi-dung:grid-cols-4">
              <div>
                {TRUONG_SAO_KE_LABEL.channelRef}: <b>{t.mapping.channelRef}</b>
              </div>
              <div>
                {TRUONG_SAO_KE_LABEL.gross}: <b>{t.mapping.gross}</b>
              </div>
              <div>
                {TRUONG_SAO_KE_LABEL.partnerShare}: <b>{t.mapping.partnerShare ?? '—'}</b>
              </div>
              <div>
                {TRUONG_SAO_KE_LABEL.net}: <b>{t.mapping.net ?? '—'}</b>
              </div>
            </dl>
          </li>
        ))}
        {(template.data ?? []).length === 0 && !template.isPending && (
          <li className="rounded-the border border-vien">
            <EmptyState title="Chưa có template nào" />
          </li>
        )}
      </ul>

      {coSua && (
        <form
          className="rounded-[12px] border border-vien p-4"
          onSubmit={(e) => {
            e.preventDefault();
            luu.mutate({
              name: form.name.trim(),
              mapping: {
                channelRef: form.channelRef.trim(),
                gross: form.gross.trim(),
                partnerShare: form.partnerShare.trim() || null,
                net: form.net.trim() || null,
                headerRow: Number(form.headerRow) || 1,
                sheet: form.sheet.trim() || null,
                delimiter: form.delimiter || ',',
              },
            });
          }}
        >
          <div className="grid grid-cols-1 gap-4 @min-[600px]/noi-dung:grid-cols-2 @min-[900px]/noi-dung:grid-cols-3">
            <O
              label="Tên template"
              value={form.name}
              onChange={(v) => {
                datForm({ ...form, name: v });
              }}
              required
            />
            <O
              label={TRUONG_SAO_KE_LABEL.channelRef}
              value={form.channelRef}
              onChange={(v) => {
                datForm({ ...form, channelRef: v });
              }}
              required
            />
            <O
              label={TRUONG_SAO_KE_LABEL.gross}
              value={form.gross}
              onChange={(v) => {
                datForm({ ...form, gross: v });
              }}
              required
            />
            <O
              label={TRUONG_SAO_KE_LABEL.partnerShare}
              value={form.partnerShare}
              onChange={(v) => {
                datForm({ ...form, partnerShare: v });
              }}
            />
            <O
              label={TRUONG_SAO_KE_LABEL.net}
              value={form.net}
              onChange={(v) => {
                datForm({ ...form, net: v });
              }}
            />
            <O
              label="Hàng tiêu đề"
              type="number"
              min={1}
              value={form.headerRow}
              onChange={(v) => {
                datForm({ ...form, headerRow: v });
              }}
            />
            <O
              label="Sheet (file XLSX)"
              value={form.sheet}
              onChange={(v) => {
                datForm({ ...form, sheet: v });
              }}
              moTa="Bỏ trống thì lấy sheet đầu tiên."
            />
            <O
              label="Dấu phân cách (file CSV)"
              value={form.delimiter}
              onChange={(v) => {
                datForm({ ...form, delimiter: v.slice(0, 1) });
              }}
            />
          </div>

          {luu.error && <p className="mt-3 text-co-ban text-bad-chu">{luu.error.message}</p>}

          <div className="mt-4">
            <Nut kieu="chinh" type="submit" dangChay={luu.isPending}>
              Lưu template
            </Nut>
          </div>
        </form>
      )}
    </>
  );
}

/* ----------------------------- Yêu cầu hỗ trợ ----------------------------- */

function TabHoTro({ partnerId, coSua }: { partnerId: string; coSua: boolean }) {
  const tao = useTaoTicket();
  const [form, datForm] = useState({ subject: '', body: '' });

  if (!coSua) {
    return (
      <p className="text-co-ban text-chu-phu">
        Cần quyền sửa ở module Network để gửi yêu cầu hỗ trợ.
      </p>
    );
  }

  return (
    <form
      className="max-w-2xl"
      onSubmit={(e) => {
        e.preventDefault();
        tao.mutate(
          { partnerId, subject: form.subject.trim(), body: form.body.trim() },
          {
            onSuccess: () => {
              datForm({ subject: '', body: '' });
            },
          },
        );
      }}
    >
      <div className="grid gap-4">
        <O
          label="Tiêu đề"
          value={form.subject}
          onChange={(v) => {
            datForm({ ...form, subject: v });
          }}
          required
        />
        <O
          label="Nội dung"
          value={form.body}
          onChange={(v) => {
            datForm({ ...form, body: v });
          }}
          nhieuDong
          required
        />
      </div>

      {tao.error && <p className="mt-3 text-co-ban text-bad-chu">{tao.error.message}</p>}
      {tao.isSuccess && <p className="mt-3 text-co-ban text-ok-chu">Đã gửi yêu cầu.</p>}

      <div className="mt-4">
        <Nut kieu="chinh" type="submit" dangChay={tao.isPending}>
          Gửi yêu cầu
        </Nut>
      </div>
    </form>
  );
}

/**
 * Form "Gán kênh" của tab Kênh.
 *
 * Tách riêng để test được: ô "Vào Network từ ngày" phải mặc định hôm nay, không
 * cho ngày tương lai và không cho sớm hơn ngày hiệu lực hợp đồng; và hợp đồng
 * phải chọn theo **khoảng ngày**, không theo chữ `status`.
 */
export function FormGanKenh({
  partnerId,
  hopDong,
  adsense = false,
}: {
  partnerId: string;
  /** Mọi hợp đồng đang sống của đối tác (bản xoá mềm đã bị API loại). */
  hopDong: PartnerContract[];
  /** Tài khoản AdSense: không có tỷ lệ riêng, nhãn ngày là "Vào từ ngày". */
  adsense?: boolean;
}) {
  const danhSachKenh = useDanhSachKenhChon();
  const gan = useGanKenh();

  const [chon, datChon] = useState('');
  const [tyLe, datTyLe] = useState('');
  const [tuNgay, datTuNgay] = useState(homNayVn());
  const [hopDongChon, datHopDongChon] = useState('');

  /*
   * Hợp đồng gắn vào link chọn theo **ngày vào**, cùng luật với API
   * (`hopDongPhuNgay` của `@qlk/shared`). Trước đây form lọc theo chữ
   * `status === 'active'`, mà chữ đó lạc hậu được: hợp đồng BerlinTomek còn
   * `pending` nên form không thấy bản nào, ô ngày mất mốc `min` và người dùng
   * không hề biết link sắp ra đời mà không có hợp đồng.
   */
  const phuNgayVao = hopDongPhuNgay(hopDong, tuNgay);
  const nhieuHopDong = phuNgayVao.length > 1;
  const khongHopDong = phuNgayVao.length === 0;
  const tenHopDong = adsense ? 'Cài đặt AdSense' : 'Hợp đồng';

  /*
   * Đổi ngày vào sau khi đã chọn hợp đồng có thể làm bản đã chọn không còn phủ
   * ngày đó. Khi ấy `<select>` rơi về mục đầu trong khi state giữ id cũ: thấy
   * một thứ mà gửi thứ khác. Nên lấy giá trị **đã lọc** cho cả ô và lượt gửi.
   */
  const hopDongGui = phuNgayVao.some((c) => c.id === hopDongChon) ? hopDongChon : '';

  // Mốc sớm nhất: bản phủ ngày đang chọn, không có thì bản sớm nhất của đối tác.
  const ngaySomNhat = (
    phuNgayVao.at(-1) ?? [...hopDong].sort((a, b) => a.startDate.localeCompare(b.startDate))[0]
  )?.startDate.slice(0, 10);

  return (
    <form
      className="mb-6 flex flex-wrap items-end gap-3 rounded-[12px] border border-vien p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!chon) return;
        if (nhieuHopDong && hopDongGui === '') return;
        gan.mutate(
          {
            channelId: chon,
            partnerId,
            /*
             * Bỏ trống thì **API tự gắn** hợp đồng phủ ngày vào — một chỗ duy
             * nhất quyết định, web không gửi phỏng đoán của mình.
             */
            ...(hopDongGui === '' ? {} : { contractId: hopDongGui }),
            sharePercent: adsense ? null : tyLe ? Number(tyLe) : null,
            // Ngày vào Network thật, không phải ngày bấm nút.
            joinedAt: `${tuNgay}T00:00:00.000Z`,
          },
          {
            onSuccess: () => {
              datChon('');
              datTyLe('');
              datTuNgay(homNayVn());
              datHopDongChon('');
            },
          },
        );
      }}
    >
      <OChonTruong
        label="Kênh"
        value={chon}
        onChange={datChon}
        options={[
          { value: '', label: 'Chọn kênh…' },
          ...(danhSachKenh.data?.items ?? []).map((k) => ({
            value: k.id,
            label: `${k.name} (${k.handle})`,
          })),
        ]}
        className="min-w-[240px]"
      />
      {!adsense && (
        <O
          label="Tỷ lệ riêng (%)"
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={tyLe}
          onChange={datTyLe}
          moTa="Bỏ trống thì dùng tỷ lệ của hợp đồng."
          className="w-[180px]"
        />
      )}
      <O
        label={adsense ? 'Vào từ ngày' : 'Vào Network từ ngày'}
        type="date"
        value={tuNgay}
        max={homNayVn()}
        {...(ngaySomNhat === undefined ? {} : { min: ngaySomNhat })}
        onChange={datTuNgay}
        moTa={
          ngaySomNhat === undefined
            ? `Ngày kênh thật sự vào ${adsense ? 'tài khoản AdSense' : 'Network'}, không phải hôm nay.`
            : `${adsense ? 'Cài đặt' : 'Hợp đồng'} hiệu lực từ ${ngayDay(ngaySomNhat)}.`
        }
        className="w-[200px]"
      />

      {/*
       * Ô "Hợp đồng" chỉ cần khi **nhiều hơn một** bản phủ ngày vào: đoán một
       * bản là đoán cả tỷ lệ chia và thuế Mỹ, nên API cũng từ chối.
       */}
      {nhieuHopDong && (
        <OChonTruong
          label={tenHopDong}
          value={hopDongGui}
          onChange={datHopDongChon}
          options={[
            { value: '', label: `Chọn ${tenHopDong.toLowerCase()}…` },
            ...phuNgayVao.map((c) => ({
              value: c.id,
              label: `${c.contractNo} · hiệu lực ${ngayDay(c.startDate)}`,
            })),
          ]}
          moTa={`${String(phuNgayVao.length)} bản cùng phủ ngày này — phải chọn.`}
          className="min-w-[280px]"
        />
      )}

      <Nut
        kieu="chinh"
        type="submit"
        dangChay={gan.isPending}
        disabled={!chon || tuNgay === '' || (nhieuHopDong && hopDongGui === '')}
      >
        Gán kênh
      </Nut>

      {/*
       * Không hợp đồng nào phủ ngày vào thì vẫn gán được (kênh vào trước khi
       * khai hợp đồng là có thật), nhưng phải nói rõ hậu quả: tỷ lệ Network về
       * 0 % và thuế Mỹ lấy mặc định.
       */}
      {khongHopDong && (
        <p role="status" className="w-full text-phu text-warn-chu">
          Chưa có hợp đồng nào phủ ngày {ngayDay(tuNgay)}. Gán bây giờ thì link{' '}
          <strong>không có hợp đồng</strong>: tỷ lệ Network tính 0% và thuế Mỹ lấy mặc định{' '}
          {THUE_MY_MAC_DINH_PCT}%. Khai hợp đồng trước, hoặc sửa lại ngày vào.
        </p>
      )}

      {gan.error && <p className="w-full text-phu text-bad-chu">{gan.error.message}</p>}
    </form>
  );
}
